import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { type CellValueChangedEvent } from "ag-grid-community";
import { ShareIcon } from "vega-icons";

import { useSession } from "@vega-api/custom/SessionContext/SessionContext";
import { usePagination } from "@vega-api/custom/utils/usePagination";
import { useGetDealProspectsPermissionView } from "@vega-api/generated/coinvest-deal-permission/coinvest-deal-permission";
import {
  getGetDealShortlistQueryKey,
  useGetDealShortlist,
  useUpdateDealShortlistItem,
} from "@vega-api/generated/coinvest-deal-shortlist/coinvest-deal-shortlist";
import { useFinaliseDealShortlist as useApproveDealShortlist } from "@vega-api/generated/coinvest-deal-shortlist-actions/coinvest-deal-shortlist-actions";
import { getGetDealTeaserEmailsSummaryV2QueryKey } from "@vega-api/generated/coinvest-deal-teaser-email-v2/coinvest-deal-teaser-email-v2";
import { type CoinvestPerson } from "@vega-api/generated/types/coinvestPerson";
import { type DealShortlistItemResponse } from "@vega-api/generated/types/dealShortlistItemResponse";
import { type ProfileResponse } from "@vega-api/generated/types/profileResponse";
import { type UpdateDealShortlistItemRequest } from "@vega-api/generated/types/updateDealShortlistItemRequest";
import { BulkActions } from "@vega-core/components/BulkActions/BulkActions";
import { useRevertEditOnError } from "@vega-core/components/DataGrid/useRevertEditOnError";
import { useRowSelection } from "@vega-core/components/DataGrid/useRowSelection";
import { useToast } from "@vega-core/components/Toast/useToast";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { useClipboard } from "@vega-core/hooks/useClipboard";
import { usePaginationState } from "@vega-core/hooks/usePaginationState";
import { m } from "@vega-core/paraglide/messages";
import { useDebounce } from "@vega-core/utils/useDebounce";
import { useTableFilters } from "@vega-modules/components/Table/TableFilters/hooks/useTableFilters";
import {
  FilterType,
  TableFilterId,
  type TableFiltersConfig,
} from "@vega-modules/components/Table/TableFilters/types";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

import { useDeleteProspect } from "../components/hooks/useDeleteProspect";
import { type ProspectWithMemberCount } from "../components/OutreachTable/hooks/useOutreachManagement";
import { TIER_OPTIONS } from "../constants/prospectConstants";
import { SEARCH_DEBOUNCE_DELAY_MS } from "../constants/searchConstants";
import { ShareTabType } from "../constants/sharePageConstants";

import { useCoInvestDealReadOnly } from "./useCoInvestDealReadOnly";
import { useDealCurrency } from "./useDealCurrency";
import { useDealName } from "./useDealName";
import { SHARE_TABLE_COL_IDS } from "./useShareTableColumns";

// #region Helpers

const profileToApproverPerson = (profile: ProfileResponse): CoinvestPerson => ({
  id: profile.id,
  fullName: { firstName: profile.firstName, lastName: profile.lastName },
  email: profile.email,
});

// #endregion

// #region Types

type PendingOptions = {
  canApproveDealShortlist?: boolean;
  clearSelectionTrigger: number;
};

type ApprovedOptions = {
  canEditDealOpportunity?: boolean;
};

type BaseReturn = {
  dealId: string;
  currency: ReturnType<typeof useDealCurrency>["currency"];
  prospectsData: ProspectWithMemberCount[];
  paginationElement: React.ReactNode;
  isLoading: boolean;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  RenderFilters: React.ReactNode;
  rowSelection: Record<string, boolean>;
  onRowSelectionChange: (newValue: Record<string, boolean>) => void;
  SelectOnlyToggle: React.ReactNode;
  selectedCount: number;
  isOpportunityOwnerModalOpen: boolean;
  selectedProspectsForOwner: ProspectWithMemberCount[];
  closeOpportunityOwnerModal: () => void;
  handleOpenOwnerModal: (prospect: ProspectWithMemberCount) => void;
  bulkActions: React.ReactNode;
  canAddShortlistItems?: boolean;
  hasUserAppliedFilters: boolean;
  showEmptyState: boolean;
  handleCellEdit: (
    event: CellValueChangedEvent<DealShortlistItemResponse>,
  ) => void;
  showRemoveConfirmation: boolean;
  selectedProspectForRemoval: string | null;
  handleRemoveIndividualProspect: (prospectId: string) => void;
  handleShowIndividualRemoveConfirmation: (prospectId: string) => void;
  closeRemoveConfirmation: () => void;
};

type PendingReturn = BaseReturn & {
  showOnlyAddedByMe: boolean;
  setShowOnlyAddedByMe: (value: boolean) => void;
  showSelectedOnly: boolean;
  clearSelection: () => void;
  approvingProspectId: string | null;
  showApprovalModal: boolean;
  openApprovalModal: () => void;
  closeApprovalModal: () => void;
  isApprovingProspects: boolean;
  handleApproveProspects: () => void;
  handleApproveSingleProspect: (prospectId: string) => void;
};

type ApprovedReturn = BaseReturn & {
  dealName: string;
  isShareModalOpen: boolean;
  selectedProspectsForShare: ProspectWithMemberCount[];
  handleShare: (prospect: ProspectWithMemberCount) => void;
  handleCloseShareModal: () => void;
  onShareModalSuccess: () => void;
  onOpportunityOwnerSuccess: () => void;
  isCopyLinkNoticeOpen: boolean;
  dismissCopyLinkNotice: () => void;
  handleCopyLink: (dealLink: string) => Promise<void>;
  isUpdateDataPointsModalOpen: boolean;
  closeUpdateDataPointsModal: () => void;
  dealOpportunityIdForEditingDataPoints: string | null;
  handleUpdateDataPoints: ((dealOpportunityId: string) => void) | undefined;
  showSelectedOnly: boolean;
  isDraftModalOpen: boolean;
  selectedProspectForDraft: ProspectWithMemberCount | null;
  handleOpenDraft: (prospect: ProspectWithMemberCount) => void;
  handleCloseDraftModal: () => void;
  onDraftSuccess: () => void;
};

// #endregion

// #region Hook overloads

export function useShareTableManagement(
  tab: ShareTabType.Pending,
  options: PendingOptions,
): PendingReturn;
export function useShareTableManagement(
  tab: ShareTabType.Approved,
  options: ApprovedOptions,
): ApprovedReturn;

// #endregion

// #region Implementation

export function useShareTableManagement(
  tab: ShareTabType,
  options: ApprovedOptions | PendingOptions,
): ApprovedReturn | PendingReturn {
  const {
    canApproveDealShortlist,
    canEditDealOpportunity,
    clearSelectionTrigger,
  } = {
    canApproveDealShortlist: undefined,
    canEditDealOpportunity: undefined,
    clearSelectionTrigger: 0,
    ...options,
  };

  const { id: dealIdFromParams } = useParams<{ id: string }>();
  const dealId = dealIdFromParams ?? "";
  const queryClient = useQueryClient();
  const showToast = useToast();
  const showErrorToast = useCoInvestErrorToast();
  const { user: currentUser } = useSession();
  const { isMutationLocked } = useCoInvestDealReadOnly();
  // Closed deals lock all shortlist mutations except the notes column
  const canMutateShortlist = !isMutationLocked;

  // #region Row selection

  // Created before the tier filter so `tierFiltersConfig.disabled` can read
  // `showSelectedOnly`; the visible rows are supplied per-call below.
  const {
    rowSelection,
    getDisplayRows,
    onSelectedRowsChange,
    showSelectedOnly,
    selectedItems,
    selectedIds,
    selectedCount,
    clearSelection,
    deselect,
    renderShowSelectedOnlyToggle,
  } = useRowSelection<ProspectWithMemberCount>({
    getRowId: (prospect) => prospect.id,
    tableId: dealId,
  });

  // #endregion

  // #region Tier filter

  const tierFiltersConfig = useMemo(
    () =>
      ({
        filters: [
          {
            id: "tier",
            type: FilterType.Dropdown,
            label: m.coInvestments_prospects_tier(),
            props: {
              placeholder: m.coInvestments_tracking_tier_placeholder({
                value: m.common_all(),
              }),
              renderValue: (label: string) =>
                m.coInvestments_tracking_tier_withValue({ label }),
              options: TIER_OPTIONS,
              className: "w-24",
            },
          },
        ],
        showResetButton: false,
        noMarginBottom: true,
        disabled: showSelectedOnly,
      }) satisfies TableFiltersConfig,
    [showSelectedOnly],
  );

  const { filterState, RenderFilters, hasActiveFilters } = useTableFilters({
    id:
      tab === ShareTabType.Pending
        ? TableFilterId.CoInvestmentsSharePending
        : TableFilterId.CoInvestmentsShareApproved,
    config: tierFiltersConfig,
    persistInSession: false,
  });

  const tierFilter = filterState.tier?.value;

  // #endregion

  // #region Search & pagination

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, SEARCH_DEBOUNCE_DELAY_MS);
  const paginationState = usePaginationState();

  // #endregion

  // #region Pending-only filters

  const [showOnlyAddedByMe, setShowOnlyAddedByMe] = useState(false);

  // #endregion

  // #region Data fetching

  const { data: pendingShortlistData, isPending: isPendingLoading } =
    useGetDealShortlist(
      dealId,
      {
        isApproved: false,
        tier: tierFilter || undefined,
        wasAddedByUser: showOnlyAddedByMe || undefined,
        searchQuery: debouncedSearch || undefined,
        page: paginationState.currentPage,
        pageSize: paginationState.currentPageSize,
      },
      { query: { enabled: !!dealId && tab === ShareTabType.Pending } },
    );

  const { data: approvedShortlistData, isPending: isApprovedLoading } =
    useGetDealShortlist(
      dealId,
      {
        isApproved: true,
        includeTeaserEmail: true,
        tier: tierFilter || undefined,
        searchQuery: debouncedSearch || undefined,
        page: paginationState.currentPage,
        pageSize: paginationState.currentPageSize,
      },
      { query: { enabled: !!dealId && tab === ShareTabType.Approved } },
    );

  const { data: permissionViewData } = useGetDealProspectsPermissionView(
    dealId,
    {
      query: {
        enabled: !!dealId && tab === ShareTabType.Approved,
        select: (response) => {
          const limitedPartners = response.data?.limitedPartners || [];

          return limitedPartners.reduce(
            (acc, lp) => {
              acc[lp.limitedPartner.id] = lp.memberCount;

              return acc;
            },
            {} as Record<string, number>,
          );
        },
      },
    },
  );

  // #endregion

  // #region Data processing

  const pendingRawData = pendingShortlistData?.data?.dealShortlistItems ?? [];

  const approvedRawData: ProspectWithMemberCount[] = useMemo(() => {
    const items = approvedShortlistData?.data?.dealShortlistItems ?? [];

    return items.map((prospect) => ({
      ...prospect,
      memberCount: permissionViewData?.[prospect.limitedPartner.id] ?? 0,
    }));
  }, [approvedShortlistData?.data?.dealShortlistItems, permissionViewData]);

  // Only one tab's table mounts at a time (SharePage swaps them), so a single
  // selection instance backs whichever tab this hook serves.
  const baseProspectsData: ProspectWithMemberCount[] =
    tab === ShareTabType.Approved ? approvedRawData : pendingRawData;

  const prospectsData = getDisplayRows(baseProspectsData);

  const handleSelectedRowsChange = (newValue: Record<string, boolean>) => {
    onSelectedRowsChange(newValue, baseProspectsData);
  };

  const SelectOnlyToggle = renderShowSelectedOnlyToggle({
    onShow: () => {
      setShowOnlyAddedByMe(false);
      setSearchQuery("");
    },
  });

  const isLoading =
    tab === ShareTabType.Pending ? isPendingLoading : isApprovedLoading;

  const { paginationElement } = usePagination({
    meta:
      (tab === ShareTabType.Pending
        ? pendingShortlistData
        : approvedShortlistData
      )?.meta ?? null,
    isLoading,
    filterDependencies:
      tab === ShareTabType.Pending
        ? [tierFilter, showOnlyAddedByMe, debouncedSearch]
        : [tierFilter, debouncedSearch],
    ...paginationState,
  });

  // #endregion

  // #region Row selection handlers

  useEffect(() => {
    if (clearSelectionTrigger > 0) {
      clearSelection();
    }
  }, [clearSelectionTrigger, clearSelection]);

  // #endregion

  // #region Approve flow

  const [approvingProspectId, setApprovingProspectId] = useState<string | null>(
    null,
  );

  const {
    isOpen: showApprovalModal,
    setTrue: openApprovalModal,
    setFalse: closeApprovalModal,
  } = useBoolean(false);

  const approveMutation = useApproveDealShortlist({
    mutation: {
      onSuccess: () => {
        showToast({
          title: m.coInvestments_prospectsApproved(),
          variant: "success",
        });
        queryClient.invalidateQueries({
          queryKey: getGetDealShortlistQueryKey(dealId),
        });
      },
      onError: (error) => {
        showErrorToast(error);
      },
    },
  });

  const handleApproveProspects = () => {
    if (!dealId || !currentUser) return;

    approveMutation.mutate(
      {
        dealId,
        data: {
          shortlistItemIds: selectedIds,
          approverPerson: profileToApproverPerson(currentUser),
        },
      },
      {
        onSuccess: () => {
          closeApprovalModal();
          clearSelection();
        },
      },
    );
  };

  const handleApproveSingleProspect = (prospectId: string) => {
    if (!dealId || !currentUser) return;

    setApprovingProspectId(prospectId);
    approveMutation.mutate(
      {
        dealId,
        data: {
          shortlistItemIds: [prospectId],
          approverPerson: profileToApproverPerson(currentUser),
        },
      },
      {
        onSuccess: () => {
          deselect([prospectId]);
        },
        onSettled: () => {
          setApprovingProspectId(null);
        },
      },
    );
  };

  // #endregion

  // #region Delete flow

  const deleteProspect = useDeleteProspect({
    dealId,
    onRemoveSuccess: (prospectId: string) => {
      deselect([prospectId]);
      queryClient.invalidateQueries({
        queryKey: getGetDealTeaserEmailsSummaryV2QueryKey(dealId),
      });
    },
    prospectsData,
  });

  // #endregion

  // #region Opportunity owner modal

  const {
    isOpen: isOpportunityOwnerModalOpen,
    setTrue: openOpportunityOwnerModal,
    setFalse: closeOpportunityOwnerModal,
  } = useBoolean(false);

  const [selectedProspectsForOwner, setSelectedProspectsForOwner] = useState<
    ProspectWithMemberCount[]
  >([]);

  const handleOpenOwnerModal = (prospect: ProspectWithMemberCount) => {
    setSelectedProspectsForOwner([prospect]);
    openOpportunityOwnerModal();
  };

  // #endregion

  // #region Currency

  const { currency } = useDealCurrency(dealId);

  // #endregion

  // #region Share modal

  const {
    isOpen: isShareModalOpen,
    setTrue: openShareModal,
    setFalse: closeShareModal,
  } = useBoolean(false);

  const [selectedProspectsForShare, setSelectedProspectsForShare] = useState<
    ProspectWithMemberCount[]
  >([]);

  const handleShare = (prospect: ProspectWithMemberCount) => {
    setSelectedProspectsForShare([prospect]);
    openShareModal();
  };

  const handleCloseShareModal = () => {
    closeShareModal();
    setSelectedProspectsForShare([]);
  };

  const handleBulkShare = () => {
    setSelectedProspectsForShare(selectedItems);
    openShareModal();
  };

  const canAllSelectedProspectsShare = selectedItems.every((prospect) => {
    const { canSendDeal, memberCount = 0, isClosed } = prospect;

    return canSendDeal && memberCount > 0 && !isClosed;
  });

  // #endregion

  // #region Draft email modal

  const {
    isOpen: isDraftModalOpen,
    setTrue: openDraftModal,
    setFalse: closeDraftModal,
  } = useBoolean(false);

  const [selectedProspectForDraft, setSelectedProspectForDraft] =
    useState<ProspectWithMemberCount | null>(null);

  const handleOpenDraft = (prospect: ProspectWithMemberCount) => {
    setSelectedProspectForDraft(prospect);
    openDraftModal();
  };

  const handleCloseDraftModal = () => {
    closeDraftModal();
    setSelectedProspectForDraft(null);
  };

  // Refetch so the row's embedded teaserEmail (the Draft/Scheduled button state) reflects the save.
  const onDraftSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: getGetDealShortlistQueryKey(dealId),
    });
    queryClient.invalidateQueries({
      queryKey: getGetDealTeaserEmailsSummaryV2QueryKey(dealId),
    });
    handleCloseDraftModal();
  };

  // #endregion

  // #region Copy link

  const { copy } = useClipboard({
    name: m.coInvestments_outreach_link(),
    withToast: true,
  });

  const {
    isOpen: isCopyLinkNoticeOpen,
    setTrue: showCopyLinkNotice,
    setFalse: dismissCopyLinkNotice,
  } = useBoolean(false);

  const handleCopyLink = async (dealLink: string) => {
    copy(dealLink);
    showCopyLinkNotice();
  };

  // #endregion

  // #region Update data points modal

  const {
    isOpen: isUpdateDataPointsModalOpen,
    setTrue: openUpdateDataPointsModal,
    setFalse: closeUpdateDataPointsModal,
  } = useBoolean(false);

  const [
    dealOpportunityIdForEditingDataPoints,
    setDealOpportunityIdForEditingDataPoints,
  ] = useState<string | null>(null);

  const handleUpdateDataPoints = canEditDealOpportunity
    ? (dealOpportunityId: string) => {
        setDealOpportunityIdForEditingDataPoints(dealOpportunityId);
        openUpdateDataPointsModal();
      }
    : undefined;

  // #endregion

  // #region Deal name

  const { dealName } = useDealName(dealId);

  // #endregion

  // #region Shortlist cell updates

  const { captureCellChangeEvent } =
    useRevertEditOnError<DealShortlistItemResponse>();

  const { mutate: updateShortlistItem } = useUpdateDealShortlistItem({
    mutation: {
      onSuccess: () => {
        showToast({ variant: "success", title: m.status_success() });
        queryClient.invalidateQueries({
          queryKey: getGetDealShortlistQueryKey(dealId),
        });
      },
      onError: (error) => {
        showErrorToast(error, { defaultMessage: m.dataGrid_updateError() });
      },
    },
  });

  const handleCellEdit = captureCellChangeEvent(
    (event: CellValueChangedEvent<DealShortlistItemResponse>, revertEdit) => {
      const item = event.data;
      const colId = event.column.getId();

      const patchShortlistItem = (data: UpdateDealShortlistItemRequest) => {
        updateShortlistItem(
          { dealId, dealShortlistItemId: item.id, data },
          { onError: revertEdit },
        );
      };

      if (colId === SHARE_TABLE_COL_IDS.notes) {
        patchShortlistItem({ notes: item.notes });
      } else if (colId === SHARE_TABLE_COL_IDS.tier) {
        patchShortlistItem({ tierLevel: Number(item.tier) });
      } else if (colId === SHARE_TABLE_COL_IDS.estimatedTicketSizeMin) {
        patchShortlistItem({
          estimatedTicketSizeMin: item.estimatedTicketSizeMin,
        });
      } else if (colId === SHARE_TABLE_COL_IDS.estimatedTicketSizeMax) {
        patchShortlistItem({
          estimatedTicketSizeMax: item.estimatedTicketSizeMax,
        });
      }
    },
  );

  // #endregion

  // #region Bulk actions

  const pendingBulkActions = (
    <BulkActions
      isLoading={isPendingLoading}
      onClear={clearSelection}
      primaryAction={
        canApproveDealShortlist && canMutateShortlist
          ? {
              children: m.coInvestments_approveProspectList(),
              onClick: openApprovalModal,
              variant: "primary",
            }
          : undefined
      }
      selectedCount={selectedCount}
      selectionLabel={{
        single: m.coInvestments_prospect(),
        multiple: m.coInvestments_prospects(),
      }}
    />
  );

  const approvedBulkActions = (
    <BulkActions
      isLoading={isApprovedLoading}
      onClear={clearSelection}
      primaryAction={
        canMutateShortlist
          ? {
              children: m.btn_share(),
              onClick: handleBulkShare,
              disabled: !canAllSelectedProspectsShare,
              icon: <ShareIcon />,
              iconPosition: "right",
            }
          : undefined
      }
      secondaryAction={
        canMutateShortlist
          ? {
              children: m.btn_addOpportunityOwner(),
              onClick: () => {
                setSelectedProspectsForOwner(selectedItems);
                openOpportunityOwnerModal();
              },
            }
          : undefined
      }
      selectedCount={selectedCount}
      selectionLabel={{
        single: m.coInvestments_prospect(),
        multiple: m.coInvestments_prospects(),
      }}
    />
  );

  // #endregion

  // #region Return

  const hasUserAppliedFilters =
    hasActiveFilters ||
    !!debouncedSearch ||
    (tab === ShareTabType.Pending && showOnlyAddedByMe);

  const showEmptyState =
    prospectsData.length === 0 && !isLoading && !hasUserAppliedFilters;

  const baseReturn: BaseReturn = {
    dealId,
    currency,
    prospectsData,
    paginationElement: showSelectedOnly ? null : paginationElement,
    isLoading,
    searchQuery,
    setSearchQuery,
    RenderFilters,
    rowSelection,
    onRowSelectionChange: handleSelectedRowsChange,
    SelectOnlyToggle,
    selectedCount,
    isOpportunityOwnerModalOpen,
    selectedProspectsForOwner,
    closeOpportunityOwnerModal,
    handleOpenOwnerModal,
    bulkActions:
      tab === ShareTabType.Pending ? pendingBulkActions : approvedBulkActions,
    hasUserAppliedFilters,
    showEmptyState,
    handleCellEdit,
    showRemoveConfirmation: deleteProspect.showRemoveConfirmation,
    selectedProspectForRemoval: deleteProspect.selectedProspectForRemoval,
    handleRemoveIndividualProspect:
      deleteProspect.handleRemoveIndividualProspect,
    handleShowIndividualRemoveConfirmation:
      deleteProspect.handleShowIndividualRemoveConfirmation,
    closeRemoveConfirmation: deleteProspect.closeRemoveConfirmation,
  };

  if (tab === ShareTabType.Pending) {
    return {
      ...baseReturn,
      showOnlyAddedByMe,
      setShowOnlyAddedByMe,
      showSelectedOnly,
      clearSelection,
      approvingProspectId,
      showApprovalModal,
      openApprovalModal,
      closeApprovalModal,
      isApprovingProspects: approveMutation.isPending,
      handleApproveProspects,
      handleApproveSingleProspect,
    } as PendingReturn;
  }

  return {
    ...baseReturn,
    dealName,
    isShareModalOpen,
    selectedProspectsForShare,
    handleShare,
    handleCloseShareModal,
    onShareModalSuccess: clearSelection,
    onOpportunityOwnerSuccess: clearSelection,
    isCopyLinkNoticeOpen,
    dismissCopyLinkNotice,
    handleCopyLink,
    isUpdateDataPointsModalOpen,
    closeUpdateDataPointsModal,
    dealOpportunityIdForEditingDataPoints,
    handleUpdateDataPoints,
    showSelectedOnly,
    isDraftModalOpen,
    selectedProspectForDraft,
    handleOpenDraft,
    handleCloseDraftModal,
    onDraftSuccess,
  } as ApprovedReturn;

  // #endregion
}

// #endregion
